import type { HistorySeries, LeaderChange, Verdict, VerdictExplanation } from '@/api/types';

/**
 * Нарушение контракта — это дефект, а не данные.
 *
 * Д-Ф16: канон гарантирует взаимоисключающие поля (например, drop_ratio и
 * rise_ratio не бывают не-null одновременно). Молчаливый выбор одного из них
 * превращает расхождение бэкенда и фронтенда в невидимое: веб покажет одно,
 * Telegram — другое, и никто не узнает. В разработке и тестах падаем громко,
 * в проде пишем в консоль и показываем то, что менее опасно для владельца.
 */
export function invariant(condition: boolean, message: string): asserts condition {
  if (condition) return;
  const text = `[контракт] ${message}`;
  // Ключ — MODE, а не DEV: под Vitest DEV истинен, и тест «в проде не роняем»
  // не смог бы отличить одно поведение от другого.
  if (import.meta.env.MODE === 'production') {
    console.error(text);
    return;
  }
  throw new Error(text);
}

/**
 * Д-В16 и Global Constraint 20: серия опознаётся ПАРОЙ.
 * Один variant_key приходит и из Tourvisor, и из Travelata — по голому
 * variant_key React увидит дубликат ключа, а группировка сольёт две разные
 * ценовые истории в одну.
 */
export function seriesKey(series: Pick<HistorySeries, 'variant_key' | 'source_id'>): string {
  return `${series.variant_key}::${series.source_id}`;
}

/**
 * Смена лидера уникальна тройкой: одна отметка времени может нести несколько.
 *
 * Тип — Pick, а не полный LeaderChange: ключ строится только по трём полям,
 * source_id концов перехода в React-ключ не входят. Полный LeaderChange заставил
 * бы места вызова (и тесты) тащить from_source_id/to_source_id, которые ключу
 * не нужны.
 */
export function leaderChangeKey(
  change: Pick<LeaderChange, 'at' | 'from_variant_key' | 'to_variant_key'>,
): string {
  return `${change.at}::${change.from_variant_key ?? '∅'}::${change.to_variant_key}`;
}

/**
 * Порядок «строгости» вердикта. Используется ТОЛЬКО для выбора наименее
 * тревожного при неоднозначности (Д-Ф13) — не для сравнения поездок между собой.
 */
export const VERDICT_SEVERITY: Record<Verdict, number> = {
  NORMAL: 0,
  WATCH: 1,
  // T24: «ниже рынка» — оценка по похожим поездкам, пока нет своей истории.
  // Строже WATCH (нам есть что сказать), но НЕ срыв: цена не падала, она
  // просто ниже, чем у соседей, и такой может быть всегда.
  DEAL: 2,
  SUSPECT: 3,
  HOT: 4,
  HOT_STRONG: 5,
};

/** Человеческие подписи вердиктов. «Ниже рынка» ≠ «подешевело» (Д-Т24-1). */
export const VERDICT_TITLES: Record<Verdict, string> = {
  NORMAL: 'обычная цена',
  WATCH: 'наблюдаем',
  DEAL: 'дешевле похожих',
  SUSPECT: 'подозрительно дёшево',
  HOT: 'цена упала',
  HOT_STRONG: 'резкое падение',
};

/** Вердикты активного срыва: их и подсвечивает вспышка --flare. */
export const BREAKDOWN_VERDICTS: readonly Verdict[] = ['HOT', 'HOT_STRONG'];

export function isBreakdownVerdict(verdict: Verdict): boolean {
  return BREAKDOWN_VERDICTS.includes(verdict);
}

/**
 * Правило достоверности §5.2: устаревший вердикт (is_stale) НЕ подсвечивается
 * как активный срыв. Вспышка --flare означает «горит сейчас»; на устаревшем
 * расчёте это ложь — цена могла уже отыграть обратно, а тик ещё не пересчитал.
 * Показать вспышку по старому вердикту = позвать владельца проверять срыв,
 * которого, возможно, уже нет.
 */
export function shouldFlare(
  explanation: Pick<VerdictExplanation, 'verdict' | 'is_stale'>,
): boolean {
  return isBreakdownVerdict(explanation.verdict) && !explanation.is_stale;
}

/**
 * Провенанс устаревания. null — вердикт свежий; строка — причина, которую
 * интерфейс обязан показать рядом, вместо тихого показа старого вердикта как
 * действующего. staleness_reason по контракту nullable, поэтому даём внятный
 * запасной текст, а не пустоту.
 */
export function stalenessNote(
  explanation: Pick<VerdictExplanation, 'is_stale' | 'staleness_reason'>,
): string | null {
  if (!explanation.is_stale) return null;
  return explanation.staleness_reason ?? 'расчёт устарел';
}

export interface VerdictChoice {
  chosen: VerdictExplanation;
  /** Вердиктов несколько, а наблюдение явно не выбрано. Интерфейс обязан сказать об этом. */
  isAmbiguous: boolean;
  alternatives: VerdictExplanation[];
}

/**
 * Д-В9: у поездки столько вердиктов, во скольких наблюдениях она участвует,
 * и пороги у наблюдений разные. Взять explanations[0] — показать вердикт
 * случайного watch.
 *
 * Правила:
 *  · явный watchId → берётся вердикт этого наблюдения, неоднозначности нет;
 *  · watchId не найден среди вердиктов → это НЕ повод молча подставить другой:
 *    выбор помечается неоднозначным, интерфейс покажет переключатель;
 *  · без watchId и при нескольких вердиктах → самый консервативный
 *    (минимальная строгость), потому что ложный HOT дороже пропущенного:
 *    владелец поедет проверять срыв, которого нет.
 */
export function pickVerdict(
  explanations: VerdictExplanation[],
  watchId: number | null,
): VerdictChoice | null {
  if (explanations.length === 0) return null;

  if (watchId !== null) {
    // findIndex, а не find: сравнение результата с undefined запрещено линтером
    // (Д-Ф15), и обходить запрет исключением ради удобства нельзя.
    const exactIndex = explanations.findIndex((item) => item.watch_id === watchId);
    if (exactIndex >= 0) {
      const exact = explanations[exactIndex]!;
      return {
        chosen: exact,
        isAmbiguous: false,
        alternatives: explanations.filter((_, index) => index !== exactIndex),
      };
    }
  }

  const conservative = explanations.reduce((least, candidate) =>
    VERDICT_SEVERITY[candidate.verdict] < VERDICT_SEVERITY[least.verdict] ? candidate : least,
  );

  return {
    chosen: conservative,
    // Неоднозначно, если вердиктов больше одного и наблюдение не выбрано точно.
    isAmbiguous: explanations.length > 1,
    alternatives: explanations.filter((item) => item !== conservative),
  };
}
